import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { validateFile, formatFileSize } from '@/utils/fileValidation';
import type {
  FileValidationResult,
  FileValidationConfig,
} from './FileUpload.types';

export const useFileValidation = (config: FileValidationConfig) => {
  const { t } = useTranslation();
  const { maxFileSize, maxFileCount, accept } = config;

  const isExtensionAccepted = useCallback(
    (file: File): boolean => {
      if (!accept) return true;
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return accept.split(',').some((a) => a.trim().toLowerCase() === ext);
    },
    [accept]
  );

  const validateSingleFile = useCallback(
    (file: File): FileValidationResult => {
      // When accept is provided, use extension-based check instead of MIME
      if (accept) {
        if (!isExtensionAccepted(file)) {
          return {
            isValid: false,
            error: t('fileUpload.unsupportedType', {
              fileName: file.name,
              fileType: file.name.split('.').pop()?.toLowerCase() ?? file.type,
              defaultValue:
                'File type {{fileType}} is not supported for {{fileName}}.',
            }),
          };
        }

        // Still check file size
        if (file.size > maxFileSize) {
          return {
            isValid: false,
            error: t('fileUpload.fileTooLarge', {
              fileName: file.name,
              fileSize: formatFileSize(file.size),
              maxSize: formatFileSize(maxFileSize),
              defaultValue:
                'File {{fileName}} is too large ({{fileSize}}). Maximum size is {{maxSize}}.',
            }),
          };
        }

        return { isValid: true };
      }

      // Fallback: use MIME-based validation with default supported types
      const result = validateFile(file, { maxFileSize });

      if (!result.isValid) {
        const firstError = result.errors[0];
        if (firstError) {
          switch (firstError.type) {
            case 'SIZE_EXCEEDED':
              return {
                isValid: false,
                error: t('fileUpload.fileTooLarge', {
                  fileName: file.name,
                  fileSize: formatFileSize(file.size),
                  maxSize: formatFileSize(maxFileSize),
                  defaultValue:
                    'File {{fileName}} is too large ({{fileSize}}). Maximum size is {{maxSize}}.',
                }),
              };
            case 'UNSUPPORTED_TYPE':
              return {
                isValid: false,
                error: t('fileUpload.unsupportedType', {
                  fileName: file.name,
                  fileType: file.type,
                  defaultValue:
                    'File type {{fileType}} is not supported for {{fileName}}.',
                }),
              };
            default:
              return {
                isValid: false,
                error: firstError.message,
              };
          }
        }
      }

      return { isValid: true };
    },
    [accept, isExtensionAccepted, maxFileSize, t]
  );

  const validateFileCount = useCallback(
    (currentCount: number, newCount: number): FileValidationResult => {
      if (
        maxFileCount !== undefined &&
        currentCount + newCount > maxFileCount
      ) {
        return {
          isValid: false,
          error: t('fileUpload.tooManyFiles', {
            maxFiles: maxFileCount,
            defaultValue: 'Cannot upload more than {{maxFiles}} files',
          }),
        };
      }
      return { isValid: true };
    },
    [maxFileCount, t]
  );

  const validateFiles = useCallback(
    (files: File[], currentFileCount: number = 0) => {
      const validFiles: File[] = [];
      const errors: string[] = [];

      const countValidation = validateFileCount(currentFileCount, files.length);
      if (!countValidation.isValid && countValidation.error) {
        return {
          validFiles: [],
          errors: [countValidation.error],
        };
      }

      files.forEach((file) => {
        const validation = validateSingleFile(file);
        if (validation.isValid) {
          validFiles.push(file);
        } else if (validation.error) {
          errors.push(validation.error);
        }
      });

      return { validFiles, errors };
    },
    [validateSingleFile, validateFileCount]
  );

  return {
    validateSingleFile,
    validateFileCount,
    validateFiles,
  };
};
